import axios from 'axios';
import { handleError } from '../utils/error';
import { AuthService } from './AuthService';
const API_URL = process.env.ANNOUNCEMENTS_API_URL;
const announcementApi = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});
class AnnouncementService extends AuthService {
    constructor() {
        super(...arguments);
        this.api = announcementApi;
    }
    /**
     * Creates a new announcement and manages optional omnichannel notifications.
     *
     * @param announcement - The properties for the new announcement.
     * @param sendNotifications - Whether to trigger automated omnichannel notifications (email, SMS, etc...).
     * @param subjectLine - Custom subject line for outbound notifications.
     * @returns - The created announcement record.
     */
    async create(announcement, sendNotifications = true, subjectLine) {
        try {
            const response = await announcementApi.post(`/announcements`, {
                announcement,
                sendNotifications,
                subjectLine
            });
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.create', error);
            throw error;
        }
    }
    /**
     * Fetches announcements matching filters.
     *
     * @param limit - Maximum number of records to retrieve.
     * @param loadMore - Cursor for pagination; returns records created before this timestamp.
     * @param itemNumber - The identifier of the product to filter by.
     * @returns - A filtered collection of announcements and the count.
     */
    async getAll(limit, loadMore, itemNumber) {
        try {
            const response = await announcementApi.get(`/announcements`, {
                params: {
                    limit: limit || undefined,
                    loadMore: loadMore || undefined,
                    itemNumber: itemNumber || undefined
                }
            });
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.getAll', error);
            throw error;
        }
    }
    /**
     * Retrieves the latest announcement for a specific item number.
     *
     * @param itemNumber - Unique identifier for the product.
     * @returns - The most recent announcement.
     */
    async getLatest(itemNumber) {
        try {
            const response = await announcementApi.get(`/announcements/latest`, {
                headers: {
                    Authorization: `Bearer ${this.getAuthToken()}`
                },
                params: {
                    itemNumber: itemNumber || undefined
                }
            });
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.getLatest', error);
            throw error;
        }
    }
    /**
     * Retrieves the unread announcement count.
     *
     * @param latest - If true, only evaluates the most recent announcement.
     * @param itemNumber - Filters the result to a specific product.
     * @returns - The total number of unread announcements.
     */
    async getUnreadCount(latest = false, itemNumber) {
        try {
            const response = await announcementApi.get(`/status/unread/count`, {
                params: {
                    latest: latest || undefined,
                    itemNumber: itemNumber || undefined
                }
            });
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.getUnreadCount', error);
            throw error;
        }
    }
    /**
     * Edits an existing announcement.
     *
     * @param id - The _id of the announcement to be edited.
     * @param announcement - The new announcement fields.
     * @returns - The edited announcement.
     */
    async editById(id, announcement) {
        try {
            const response = await announcementApi.put(`/announcements/${id}`, announcement);
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.editById', error);
            throw error;
        }
    }
    /**
     * Marks a specific announcement as read for the current user.
     *
     * @param id - The announcement id.
     * @returns - Success message.
     */
    async markAsRead(id) {
        try {
            const response = await announcementApi.post(`/status/read/${id}`);
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.markAsRead', error);
            throw error;
        }
    }
    /**
     * Marks all announcements as read for the current user.
     *
     * @returns - Success message.
     */
    async markAllAsRead() {
        try {
            const response = await announcementApi.post(`/status/read-all`);
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.markAllAsRead', error);
            throw error;
        }
    }
    /**
     * Syncs the global user record with the announcement service user record.
     *
     * @param user - The global user document.
     * @returns - The synced user.
     */
    async syncUser(user) {
        try {
            const response = await announcementApi.post(`/sync-user`, user);
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.syncUser', error);
            throw error;
        }
    }
    /**
     * Deletes an announcement.
     *
     * @param id - The announcement id.
     * @returns - The deleted announcement.
     */
    async delete(id) {
        try {
            const response = await announcementApi.delete(`/announcements/${id}`);
            return response;
        }
        catch (error) {
            handleError('AnnouncementService.delete', error);
            throw error;
        }
    }
}
export const announcementService = new AnnouncementService();
//# sourceMappingURL=AnnouncementService.js.map